/**
 * LedgerBoard — the LEDGER leaderboard source's renderer, built to the RTTF
 * anchor's board spec (rttf-drop-hub.html: .lbnav/.lb__key/.lb__r/.lb__me/
 * .lb__pg/.myr/.ptsm). Split out of LeaderboardBlock: it shares nothing with
 * the generic skin but the early-return branch that mounts it.
 *
 * Structure is the anchor's; every color routes through hub theme tokens.
 */
import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { LeaderboardPeriodType } from "@devour/client";
import { SurfaceLeaderboardBlockConfigResponse } from "@/hooks/hub/useGetHub";
import { useGetLedgerStandings } from "@/hooks/ledger-boards/useGetLedgerStandings";
import { useGetMyLedgerContributions } from "@/hooks/ledger-boards/useGetMyLedgerContributions";
import { useJoinLedgerBoardRoom } from "@/hooks/ledger-boards/useJoinLedgerBoardRoom";
import { useGetBoardPrizes } from "@/hooks/ledger-boards/useGetBoardPrizes";
import "./LedgerBoard.scss";

/** Window labels for the ledger board's tab nav, keyed by LeaderboardPeriodType */
const LEDGER_WINDOW_LABELS: Record<string, { current: string; previous: string }> = {
    DAILY: { current: "Today", previous: "Yesterday" },
    WEEKLY: { current: "This Week", previous: "Last Week" },
    MONTHLY: { current: "This Month", previous: "Last Month" },
};

/*
 * Detail labels are reward NAMES, which admins prefix with the campaign
 * ("RTTF — Added a Find") to keep their own lists sortable. The member is
 * already ON the campaign, so a prefix every row shares is dropped — and only
 * when every row shares it, so mixed sources never lose meaning.
 */
function stripSharedPrefix<T extends { label: string }>(rows: T[]): T[] {
    if (rows.length < 2) return rows;
    const sep = " \u2014 ";
    const first = rows[0].label;
    const cut = first.indexOf(sep);
    if (cut < 0) return rows;
    const prefix = first.slice(0, cut + sep.length);
    if (!rows.every(r => r.label.startsWith(prefix))) return rows;
    return rows.map(r => ({...r, label: r.label.slice(prefix.length)}));
}

/** "Resets in 4d 6h" — the anchor's pager status for the live window */
function formatResetsIn(endsAt: number): string {
    const ms = endsAt - Date.now();
    if (ms <= 60_000) return "Resets soon";
    const days = Math.floor(ms / 86_400_000);
    const hours = Math.floor(ms % 86_400_000 / 3_600_000);
    if (days > 0) return `Resets in ${days}d ${hours}h`;
    const minutes = Math.floor(ms % 3_600_000 / 60_000);
    return `Resets in ${hours}h ${minutes}m`;
}

/*
 * "prizes" is a WINDOW, not a dialog. What the board pays is another view of
 * the same board — the tab swaps the table for the ladder in place, so a member
 * reads it without losing their position behind a modal.
 */
type LedgerWindow = "current" | "previous" | "all" | "prizes";

/*
 * Prizes is a VIEW, not a period. Everything that reads the standings reads
 * this instead — otherwise opening the ladder silently re-points the query at
 * the previous period and the rank strip above it starts quoting numbers from
 * a period nobody selected.
 */
type LedgerPeriodWindow = Exclude<LedgerWindow, "prizes">;

export const LedgerBoard = ({ config, sectionTitle }: { config: SurfaceLeaderboardBlockConfigResponse; sectionTitle?: string }) => {
    const maxRows = config.maxRows || 10;
    const [win, setWin] = useState<LedgerWindow>("current");
    /* The period the table is showing — unchanged while the prize ladder is open. */
    const [periodWin, setPeriodWin] = useState<LedgerPeriodWindow>("current");
    /* The fold's `aria-controls` needs a target, and a page can carry two boards. */
    const boardId = `lgb-${config.ledgerBoardId}`;
    const [page, setPage] = useState(0);
    const [breakdownOpen, setBreakdownOpen] = useState(false);
    const {data: prizes} = useGetBoardPrizes(config.ledgerBoardId);
    /* Collapses the whole section, at every viewport — see `--folded` in the SCSS. */
    const [folded, setFolded] = useState(false);
    /* Dialog focus: close button takes focus on open; the trigger gets it back on close */
    const breakdownCloseRef = useRef<HTMLButtonElement>(null);
    const breakdownTriggerRef = useRef<HTMLButtonElement>(null);
    const breakdownScrimRef = useRef<HTMLButtonElement>(null);
    /*
     * The current period's index, learned from the current-window response —
     * "previous" is that index minus one, requestable because closed periods
     * are durable rows on the server.
     */
    const [currentIndex, setCurrentIndex] = useState<number | null>(null);

    const periodKey = periodWin === "current"
        ? undefined
        : periodWin === "all"
            ? ""
            : currentIndex != null && currentIndex >= 1 ? String(currentIndex - 1) : undefined;
    const { data, isLoading, error, isPlaceholderData } = useGetLedgerStandings(config.ledgerBoardId, {
        periodKey,
        limit: maxRows,
        offset: page * maxRows,
    });

    /* "Resets in" re-reads the clock each render; this re-renders once a minute so it counts down while open. */
    const [, setMinuteTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setMinuteTick(t => t + 1), 60_000);
        return () => clearInterval(id);
    }, []);

    /* Live: the board refetches the moment anyone's contribution lands. */
    useJoinLedgerBoardRoom(config.ledgerBoardId);

    /*
     * FLIP: rows slide to their new places when the live refetch reorders
     * them. Refs and direct style writes only — a reorder costs no re-render.
     */
    const rowEls = useRef<Map<string, HTMLElement>>(new Map());
    const rowTops = useRef<Map<string, number>>(new Map());
    const shownList = useRef<string>("");

    useLayoutEffect(() => {
        /* JS-driven, so a CSS media query cannot opt out for us. */
        const reduced = typeof window !== "undefined" &&
            window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        /*
         * Keyed on the period the SERVER echoed, not the one requested —
         * `keepPreviousData` means the DOM still shows the old period on the
         * pass where the request changes, and keying on the request would mark
         * that pass as a new list and the next one as the same list, sliding
         * every member who appears in both from one period's rank to the
         * other's.
         */
        const listKey = `${data?.periodKey ?? "?"}:${page}`;
        const sameList = listKey === shownList.current;
        shownList.current = listKey;

        /*
         * Offsets are relative to the ROWS container, not the card. `offsetTop`
         * measures from `.lgb`, so anything above the list appearing later —
         * the period tabs once the current index resolves, the prize panel, the
         * my-rank strip — shifted every row by the same amount and the next
         * update slid the whole board as one phantom move.
         */
        const first = rowEls.current.values().next().value as HTMLElement | undefined;
        const base = first?.parentElement?.offsetTop ?? 0;
        const next = new Map<string, number>();
        const moved: Array<{ el: HTMLElement; from: number; to: number }> = [];
        /* Measure everything BEFORE writing anything — interleaving forces a reflow per row. */
        for (const [key, el] of rowEls.current) {
            /*
             * A collapsed board is `display: none`, where offsetTop is 0 for
             * every row. Recording that would make the whole list slide in from
             * the top on the next update, so keep the last real positions.
             */
            if (el.offsetParent === null) return;
            const top = el.offsetTop - base;
            next.set(key, top);
            const was = rowTops.current.get(key);
            if (!reduced && sameList && was !== undefined && was !== top) {
                moved.push({el, from: was, to: top});
            }
        }
        rowTops.current = next;
        if (!moved.length) return;

        for (const {el, from, to} of moved) {
            el.style.transition = "none";
            el.style.transform = `translateY(${from - to}px)`;
            /* Climbing rows pass OVER the ones they overtake, not under. */
            el.style.zIndex = from > to ? "1" : "";
        }
        /*
         * Commit the inverted state. Without this the browser can coalesce the
         * invert and the release into one style recalc, no transition starts,
         * and the row teleports.
         */
        document.body.getBoundingClientRect();

        const frame = requestAnimationFrame(() => {
            for (const {el, from, to} of moved) {
                /* Longer moves get longer — one duration for every distance reads as a smear. */
                const ms = Math.min(620, 260 + Math.abs(from - to) * 0.9);
                el.style.transition = `transform ${ms}ms cubic-bezier(0.16, 1, 0.3, 1)`;
                el.style.transform = "";
            }
        });
        const settle = window.setTimeout(() => {
            for (const {el} of moved) {
                el.style.transition = "";
                el.style.transform = "";
                el.style.zIndex = "";
            }
        }, 700);

        return () => {
            cancelAnimationFrame(frame);
            clearTimeout(settle);
            /*
             * Clear, don't just cancel. A second update landing before the
             * release frame would otherwise strand the row mid-invert, offset
             * over its neighbour until something happened to move it again.
             */
            for (const {el} of moved) {
                el.style.transition = "";
                el.style.transform = "";
                el.style.zIndex = "";
            }
        };
    }, [data, page]);

    useEffect(() => {
        /*
         * `!isPlaceholderData`: the query keeps the last period's payload on
         * screen through a switch, so returning to Current would otherwise
         * learn the index from the period the member just left — and every
         * period tab under it would then point one period too far back.
         */
        if (periodWin === "current" && !isPlaceholderData && data?.periodKey) {
            const n = Number(data.periodKey);
            if (Number.isFinite(n)) {
                setCurrentIndex(n);
            }
        }
    }, [periodWin, isPlaceholderData, data?.periodKey]);

    const labels = LEDGER_WINDOW_LABELS[data?.periodType ?? ""] ?? { current: "Current", previous: "Previous" };
    const isOneTime = data?.periodType === LeaderboardPeriodType.ONE_TIME;
    const showTabs = !isOneTime && (data?.showAllTime || currentIndex != null && currentIndex >= 1);
    const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / maxRows));

    /*
     * THE FIELD CAN SHRINK UNDER A MEMBER WHO HAS PAGED INTO IT. A reset takes
     * a 24-row board to 3, the poll refetches the page they were on, and an
     * offset past the end returns nothing — leaving "No rankings yet" with the
     * pager unrendered and no control on screen that can move them back.
     */
    useEffect(() => {
        if (page > 0 && page >= totalPages) setPage(0);
    }, [page, totalPages]);

    /* MY POINTS breakdown — fetched only while the modal is open, for the viewed window */
    const { data: breakdownData, isLoading: breakdownLoading } = useGetMyLedgerContributions(
        breakdownOpen ? config.ledgerBoardId : undefined,
        { periodKey },
    );

    /*
     * A BOARD THAT IS NOT PUBLIC DOES NOT SHOW — it does not apologise.
     *
     * Switched off, or not yet started, the read answers 404. That is the
     * board's own "I am not public", not a failure, and rendering "Unable to
     * load leaderboard" in its place put an error on a member's screen for a
     * deliberate operator decision. Render nothing and the section is simply
     * absent. Anything else that fails IS a failure and still reports one.
     *
     * Computed ABOVE the modal's effect, because the effect has to know it. The
     * board polls every 60s, so it can go 404 WHILE a member has the breakdown
     * open — and an effect keyed on `breakdownOpen` alone would not re-run when
     * the component stops rendering, leaving the page scroll-locked and every
     * Tab swallowed with nothing on screen to explain it.
     */
    const notPublic = (error as { response?: { status?: number } } | null)?.response?.status === 404;
    const modalOpen = breakdownOpen && !notPublic;

    useEffect(() => {
        if (!modalOpen) {
            return;
        }
        breakdownCloseRef.current?.focus();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setBreakdownOpen(false);
                return;
            }
            /*
             * `aria-modal` says the page behind is unreachable, so Tab keeps
             * that promise. The dialog's focusable elements are the scrim and
             * the close button, in that DOM order — without this, one Tab lands
             * on a control behind the scrim that the scroll lock cannot even
             * bring into view.
             */
            if (e.key === "Tab") {
                const stops = [breakdownScrimRef.current, breakdownCloseRef.current].filter(Boolean) as HTMLElement[];
                if (stops.length === 0) return;
                e.preventDefault();
                const at = stops.indexOf(document.activeElement as HTMLElement);
                const next = (at + (e.shiftKey ? -1 : 1) + stops.length) % stops.length;
                stops[next].focus();
            }
        };
        window.addEventListener("keydown", onKey);
        /* The page must not move under an open modal, even at the list's end. */
        const priorOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = priorOverflow;
            breakdownTriggerRef.current?.focus();
        };
    }, [modalOpen]);

    const pickWindow = (next: LedgerWindow) => {
        setWin(next);
        if (next !== "prizes") {
            setPeriodWin(next);
            setPage(0);
        }
    };

    if (notPublic) return null;

    return (
        <div className="leaderboard-block leaderboard-block--ledger">
            <div id={boardId} className={`lgb ${folded ? "lgb--folded" : ""}`}>
                {/*
                  * THE BLOCK'S HEADER ROW — the section's name on the left, the
                  * control that collapses the whole section on the right. What
                  * the board PAYS is one tap away in the Prizes view, not up
                  * here: the header names the section, the row below it answers
                  * "where am I", and a header trying to say all three is how a
                  * bar gets crowded.
                  */}
                <div className="lgb__hdr">
                    <span className="lgb__hdr-t">
                        <i className="lgb__hdr-i" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M8 4h8v5a4 4 0 0 1-8 0z" />
                                <path d="M8 5H5v2a3 3 0 0 0 3 3M16 5h3v2a3 3 0 0 1-3 3" />
                                <path d="M12 13v4M9 20h6" />
                            </svg>
                        </i>
                        {/* The block's authored title, like every other leaderboard source
                            renders it. "Leaderboard" is the fallback, not the rule — this
                            board was the one place an authored title was silently dropped. */}
                        <b>{sectionTitle || "Leaderboard"}</b>
                    </span>
                    {/*
                      * COLLAPSE THE WHOLE BLOCK. It lived on the end of the tab
                      * row until the Prizes tab took that slot; the header is
                      * where it belongs anyway, since it folds the section
                      * rather than the periods.
                      */}
                    <button
                        type="button"
                        className="lgb__fold"
                        aria-expanded={!folded}
                        aria-controls={boardId}
                        aria-label={folded ? "Expand leaderboard" : "Collapse leaderboard"}
                        onClick={() => setFolded(f => !f)}
                    >
                        {folded ? "+" : "−"}
                    </button>
                </div>

                {/*
                  * MY RANK / MY POINTS strip — the anchor's .myr, directly under
                  * the header and ABOVE the view row, with the View Points
                  * breakdown trigger on its end.
                  */}
                {/*
                  * Keyed on POINTS, not rank. A member who has not scored this
                  * period has 0 points and no rank — and gating on rank took
                  * the whole strip away at every rollover, hiding the VIEW
                  * POINTS trigger exactly when the breakdown is most useful.
                  */}
                {data?.myPoints != null &&
                    <div className="lgb__myrw"><div className="lgb__myr">
                        <span className="lgb__myr-c"><span>Rank</span><b>{data.myRank != null ? `#${data.myRank}` : "—"}</b></span>
                        <span className="lgb__myr-c"><span>Points</span><b>{(data.myPoints ?? 0).toLocaleString()}</b></span>
                        <button type="button" ref={breakdownTriggerRef} className="lgb__myr-go" onClick={() => setBreakdownOpen(true)}>
                            View Points <i aria-hidden="true">›</i>
                        </button>
                    </div></div>
                }

                {/*
                  * The row shows for a board with more than one period OR a
                  * board that pays — a ONE_TIME contest has a single period and
                  * a fixed ladder, and gating the ladder on the period tabs put
                  * its prizes out of reach entirely.
                  *
                  * These are pressable buttons, not ARIA tabs: the views they
                  * swap are siblings in this card, not tabpanels, and claiming
                  * a relationship a screen reader cannot then follow is worse
                  * than claiming none.
                  */}
                {/* `> 0`, not truthiness: a board with no tiers answers 0, and `false || 0` renders a bare "0". */}
                {(showTabs || (prizes?.payingPlaces ?? 0) > 0) &&
                    <div className="lgb__nav">
                        <div className="lgb__tabs" role="group" aria-label="Leaderboard view">
                            <button
                                type="button"
                                aria-pressed={win === "current"}
                                className={`lgb__tab ${win === "current" ? "lgb__tab--active" : ""}`}
                                onClick={() => pickWindow("current")}
                            >
                                {labels.current}
                            </button>
                            {showTabs && currentIndex != null && currentIndex >= 1 &&
                                <button
                                    type="button"
                                    aria-pressed={win === "previous"}
                                    className={`lgb__tab ${win === "previous" ? "lgb__tab--active" : ""}`}
                                    onClick={() => pickWindow("previous")}
                                >
                                    {labels.previous}
                                </button>
                            }
                            {showTabs && data?.showAllTime &&
                                <button
                                    type="button"
                                    aria-pressed={win === "all"}
                                    className={`lgb__tab ${win === "all" ? "lgb__tab--active" : ""}`}
                                    onClick={() => pickWindow("all")}
                                >
                                    All-Time
                                </button>
                            }
                            {/*
                              * PRIZES, in the slot the fold control used to
                              * hold. The ladder is another view of this board,
                              * so it sits in the row rather than in a dialog —
                              * and it only exists when the board actually pays.
                              */}
                            {prizes?.payingPlaces
                                ? <button
                                    type="button"
                                    aria-pressed={win === "prizes"}
                                    className={`lgb__tab ${win === "prizes" ? "lgb__tab--active" : ""}`}
                                    onClick={() => pickWindow("prizes")}
                                >
                                    {/*
                                      * The board's own word, not ours. Minelab
                                      * went digital-only and "Prizes" became a
                                      * legal problem — a compiled-in label made
                                      * that a release for one word.
                                      */}
                                    {data?.rewardsNoun ?? "Rewards"}
                                </button>
                                : null}
                        </div>
                    </div>
                }

                {/*
                  * THE PRIZE LADDER, IN PLACE OF THE TABLE. Selecting Prizes
                  * swaps this board's rows for what it pays — the same board,
                  * a different question — so nothing below has to load and the
                  * member keeps their place in the block.
                  */}
                {win === "prizes" && prizes &&
                    <div className="lgb__przp">
                        <div className="lgb__przp-hd">
                            Top {prizes.payingPlaces} places win
                        </div>
                        {prizes.tiers.map(tier =>
                            <div className="lgb__prz-tier" key={`${tier.rankStart}-${tier.rankEnd}`}>
                                <span className="lgb__prz-rank">
                                    {tier.rankStart === tier.rankEnd
                                        ? `#${tier.rankStart}`
                                        : `#${tier.rankStart}–${tier.rankEnd}`}
                                </span>
                                <div className="lgb__prz-items">
                                    {tier.prizes.map(prize =>
                                        <span className="lgb__prz-item" key={prize.id}>
                                            {/* Art when the reward has it; a named prize with none is still a prize. */}
                                            {prize.imageUrl &&
                                                <img src={prize.imageUrl} alt="" loading="lazy" />
                                            }
                                            <span>{prize.name}</span>
                                        </span>)}
                                </div>
                            </div>)}
                    </div>
                }

                {win !== "prizes" && isLoading &&
                    <div className="leaderboard-block__skeleton">
                        {Array.from({ length: Math.min(maxRows, 5) }).map((_, i) =>
                            <div key={i} className="leaderboard-block__skeleton-row" style={{ animationDelay: `${i * 0.1}s` }} />)}
                    </div>
                }

                {win !== "prizes" && !isLoading && error != null && !notPublic &&
                    <div className="leaderboard-block__error">
                        Unable to load leaderboard. Please try again later.
                    </div>
                }

                {win !== "prizes" && !isLoading && error == null && (data?.standings.length ?? 0) === 0 &&
                    <div className="lgb__empty">
                        No rankings yet. Be the first to compete!
                    </div>
                }

                {win !== "prizes" && !isLoading && error == null && (data?.standings.length ?? 0) > 0 && <>
                    <div className="lgb__key"><span>#</span><span>{data?.memberNoun ?? "Player"}</span><span>Points</span></div>
                    <div className="lgb__rows">
                        {data!.standings.map((row) => {
                            const initial = row.displayName?.charAt(0)?.toUpperCase() || "?";
                            return <div
                                key={row.rowId}
                                /* Stable per member — NOT rank, or the node never moves. */
                                ref={el => {
                                    if (el) rowEls.current.set(row.rowId, el);
                                    else rowEls.current.delete(row.rowId);
                                }}
                                className={`lgb__r ${row.rank <= 3 ? "lgb__r--top" : ""} ${row.isMe ? "lgb__r--mine" : ""}`}
                            >
                                <span className="lgb__p">{row.rank}</span>
                                <span className="lgb__n">
                                    {/* Real avatar when the row carries one; the initial is the fallback (RTTR board). */}
                                    <span className="lgb__a">
                                        {/*
                                              * A missing file falls back to the monogram. `cdnUrl` is
                                              * always the `_c` crop (Asset.prepareForResponse), and that
                                              * crop was never generated for some older uploads — without
                                              * this the row shows a broken-image glyph.
                                              */}
                                        {row.avatarUrl
                                            ? <img
                                                src={row.avatarUrl}
                                                alt=""
                                                onError={e => {
                                                    (e.currentTarget as HTMLImageElement).style.display = "none";
                                                }}
                                            />
                                            : initial}
                                    </span>
                                    <span className="lgb__nm">
                                        {row.displayName}
                                        {/*
                                              * The placement badge mark (anchor :789, `.lb__bg`):
                                              * a scalloped rosette, NOT a disc — a circle with a
                                              * number in it is a notification count, which is the
                                              * one thing a badge must not be mistaken for. One
                                              * chip, one number: the member's badge total; rows
                                              * without badges carry nothing (anchor rows 4-10).
                                              */}
                                        {(row.badgeCount ?? 0) > 0 &&
                                                <span className="lgb__bg" aria-label={`${row.badgeCount} badges`}>
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
                                                        <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
                                                    </svg>
                                                    <b>{row.badgeCount}</b>
                                                </span>
                                        }
                                    </span>
                                </span>
                                <span className="lgb__pt">{row.points.toLocaleString()}</span>
                            </div>;
                        })}
                    </div>

                    {/*
                  * The caller's row, pinned under the field — the anchor's
                  * rationale is "so you never scroll to find yourself", which
                  * only applies when your row is NOT already on screen; when
                  * it is, the inline --mine highlight marks it instead.
                  */}
                    {data!.myRank != null && !data!.standings.some(r => r.isMe) &&
                        <div className="lgb__r lgb__me">
                            <span className="lgb__p">{data!.myRank}</span>
                            <span className="lgb__n">
                                <span className="lgb__a">★</span>
                                <span className="lgb__nm">You</span>
                            </span>
                            <span className="lgb__pt">{(data!.myPoints ?? 0).toLocaleString()}</span>
                        </div>
                    }

                    <div className="lgb__pg">
                        <span className="lgb__pgs">
                            {periodWin === "previous"
                                ? "Final"
                                : periodWin === "all"
                                    ? "All-Time"
                                    : data!.periodEndsAt != null ? formatResetsIn(data!.periodEndsAt) : labels.current}
                        </span>
                        {totalPages > 1 &&
                            <span className="lgb__pgn">
                                <button
                                    type="button"
                                    className="lgb__pgb"
                                    aria-label="Previous page"
                                    disabled={page === 0}
                                    onClick={() => setPage(p => Math.max(0, p - 1))}
                                >
                                    ‹
                                </button>
                                <span className="lgb__pgx">{page + 1} / {totalPages}</span>
                                <button
                                    type="button"
                                    className="lgb__pgb"
                                    aria-label="Next page"
                                    disabled={page + 1 >= totalPages}
                                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                >
                                    ›
                                </button>
                            </span>
                        }
                    </div>
                </>}
            </div>

            {/*
              * MY POINTS — the anchor's ptsm modal, exactly: fixed scrim over
              * the page, a centered notch-ringed panel, "My points" kicker,
              * the big total with the window word beside it, then EVERY
              * earnable action as a row — earned bold, zeros dimmed — because
              * "a total tells a member where they are; this tells them what
              * moved it, which is the only thing that tells them what to do
              * next" (anchor rttf-drop-hub.html).
              */}
            {breakdownOpen &&
                <div className="lgb__ptsm" role="dialog" aria-modal="true" aria-label="My points breakdown">
                    <button type="button" ref={breakdownScrimRef} className="lgb__ptsm-scrim" aria-label="Close" onClick={() => setBreakdownOpen(false)} />
                    <div className="lgb__ptsm-frame">
                        <div className="lgb__ptsm-card">
                            <button type="button" ref={breakdownCloseRef} className="lgb__ptsm-x" aria-label="Close" onClick={() => setBreakdownOpen(false)}>×</button>
                            <span className="lgb__ptsm-k">My points</span>
                            <div className="lgb__ptsm-t">
                                <b>{(breakdownData?.totalPoints ?? data?.myPoints ?? 0).toLocaleString()}</b>
                                <span>{periodWin === "previous" ? labels.previous.toLowerCase() : periodWin === "all" ? "all-time" : labels.current.toLowerCase()}</span>
                            </div>
                            {breakdownLoading &&
                                <div className="lgb__ptsm-empty">Loading…</div>
                            }
                            {!breakdownLoading &&
                                <div className="lgb__ptsm-rows">
                                    {stripSharedPrefix(breakdownData?.breakdown ?? []).map(row =>
                                        <div key={`${row.attributionId}:${row.label}`} className={`lgb__ptsm-row${row.points === 0 ? " lgb__ptsm-row--zero" : ""}`}>
                                            <span className="lgb__ptsm-label">{row.label}</span>
                                            <b className="lgb__ptsm-pts">{row.points.toLocaleString()}</b>
                                        </div>)}
                                </div>
                            }
                            {periodWin === "current" && data?.periodEndsAt != null &&
                                <div className="lgb__ptsm-f">{formatResetsIn(data.periodEndsAt)}</div>
                            }
                        </div>
                    </div>
                </div>
            }
        </div>
    );
};
